import { defineUi } from '@dashboard/module-contract/ui';
import type { SitesRole } from '../manifest';

export const ui = defineUi<SitesRole>({
	routes: [
		{
			path: '',
			title: 'Sites',
			requiredRole: 'sites:reader',
			component: () => import('./views/sites.vue'),
		},
		{
			// Every module's settings live at the same path (`D109`).
			path: 'settings',
			title: 'Settings',
			requiredRole: 'sites:admin',
			component: () => import('./views/settings.vue'),
		},
		{
			/**
			 * One site, with no nav entry — reached from the list and by its
			 * URL (`D119`). A site has an identity worth linking to, and it is
			 * where its content, redirects and alias domains will live.
			 *
			 * `:site` rather than `sites/:site`: the module is already mounted
			 * at `/m/sites`, so the longer form reads `/m/sites/sites/<id>`.
			 * **Declared after `settings`**, which is the other single-segment
			 * path here — vue-router ranks a static segment above a parameter,
			 * but relying on that silently is how `/m/sites/settings` ends up
			 * rendering a site called "settings".
			 */
			path: ':site',
			title: 'Site',
			requiredRole: 'sites:reader',
			component: () => import('./views/site-overview.vue'),
		},
		/**
		 * The site's other sections (`D148`).
		 *
		 * **Siblings, not children.** The contract declares a flat list of
		 * routes and the core maps each to one record; nesting would be a
		 * change to every module's contract to serve one page. Each renders
		 * `site-frame.vue` around itself, which is where the heading, the
		 * state and the tabs live.
		 *
		 * Two segments, so none of these can be confused with `settings` or
		 * with a site called `content`.
		 */
		{
			path: ':site/content',
			title: 'Content',
			requiredRole: 'sites:reader',
			component: () => import('./views/site-content.vue'),
		},
		{
			path: ':site/routing',
			title: 'Routing',
			requiredRole: 'sites:reader',
			component: () => import('./views/site-routing.vue'),
		},
		{
			path: ':site/types',
			title: 'Content types',
			requiredRole: 'sites:reader',
			component: () => import('./views/site-types.vue'),
		},
		{
			path: ':site/health',
			title: 'Health',
			requiredRole: 'sites:reader',
			component: () => import('./views/site-health.vue'),
		},
	],
});
